// Beschriftungen und Formatierung der Abrechnung (ADR-0003).
import type {
  BankEventStatus,
  BillingModel,
  CancellationRule,
  CheckoutProvider,
  ContingentMode,
  MandateStatus,
  PaymentOrderStatus,
  PaymentRunStatus,
  PeriodAnchor,
  PostingKind,
  RefundStatus,
  SepaMandate,
  TaxMode,
  Tariff,
  UnitEntryKind,
} from './api/billing';

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'info' | 'outline';

export const BILLING_MODEL_LABEL: Record<BillingModel, string> = {
  periodic: 'Beitrag',
  contingent: 'Kontingent',
};

export const BILLING_MODEL_HINT: Record<BillingModel, string> = {
  periodic: 'Regelmäßiger Beitrag – monatlich, quartalsweise, halbjährlich oder jährlich.',
  contingent: 'Pakete aus Unterrichtseinheiten, per Vorkasse, in Raten oder nach Verbrauch.',
};

export const INTERVALS = [1, 3, 6, 12] as const;

export const INTERVAL_LABEL: Record<number, string> = {
  1: 'monatlich',
  3: 'quartalsweise',
  6: 'halbjährlich',
  12: 'jährlich',
};

export const PERIOD_LABEL: Record<number, string> = {
  1: 'Monat',
  3: 'Quartal',
  6: 'Halbjahr',
  12: 'Jahr',
};

export const PERIOD_ANCHOR_LABEL: Record<PeriodAnchor, string> = {
  calendar: 'Kalenderjahr',
  school_year: 'Schuljahr',
  contract_start: 'Ab Vertragsbeginn',
};

export const CANCELLATION_RULE_LABEL: Record<CancellationRule, string> = {
  none: 'Keine Erstattung',
  credit: 'Gutschrift für die ausgefallene Stunde',
  makeup: 'Ersatztermin',
};

export const CONTINGENT_MODE_LABEL: Record<ContingentMode, string> = {
  prepaid: 'Vorkasse',
  installments: 'Raten',
  usage_based: 'Nach Verbrauch',
};

export const TAX_MODE_LABEL: Record<TaxMode, string> = {
  regelbesteuert: 'Regelbesteuert',
  kleinunternehmer: 'Kleinunternehmer (§ 19 UStG)',
  frei_4_21: 'Umsatzsteuerfrei (§ 4 Nr. 21 UStG)',
};

export const PROVIDER_LABEL: Record<string, string> = {
  stripe: 'Stripe',
  invoice: 'Rechnung / Überweisung',
  hausbank_sepa: 'Hausbank (SEPA)',
};

export const POSTING_KIND_LABEL: Record<PostingKind, string> = {
  charge: 'Forderung',
  fee: 'Gebühr',
  credit: 'Gutschrift',
  payment: 'Zahlung',
  payment_return: 'Rücklastschrift',
  write_off: 'Ausbuchung',
  refund: 'Erstattung',
  reversal: 'Storno',
};

export const RUN_STATUS: Record<PaymentRunStatus, { label: string; variant: BadgeVariant }> = {
  draft: { label: 'Entwurf', variant: 'outline' },
  approved: { label: 'Freigegeben', variant: 'info' },
  exported: { label: 'Datei erzeugt', variant: 'info' },
  submitted: { label: 'Eingereicht', variant: 'warning' },
  settled: { label: 'Abgeschlossen', variant: 'success' },
  cancelled: { label: 'Abgebrochen', variant: 'secondary' },
};

export const ORDER_STATUS: Record<PaymentOrderStatus, { label: string; variant: BadgeVariant }> = {
  planned: { label: 'Geplant', variant: 'outline' },
  submitted: { label: 'Eingereicht', variant: 'warning' },
  settled: { label: 'Bezahlt', variant: 'success' },
  failed: { label: 'Fehlgeschlagen', variant: 'destructive' },
  returned: { label: 'Zurückgegeben', variant: 'destructive' },
  cancelled: { label: 'Abgebrochen', variant: 'secondary' },
};

// SEPA-Rückgabegründe (ISO 20022), die in der Praxis am häufigsten vorkommen.
export const RETURN_REASONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'AM04', label: 'AM04 – Keine Deckung' },
  { code: 'MD06', label: 'MD06 – Widerspruch des Zahlers' },
  { code: 'MS02', label: 'MS02 – Zahler lehnt ab' },
  { code: 'AC04', label: 'AC04 – Konto erloschen' },
  { code: 'AC06', label: 'AC06 – Konto gesperrt' },
  { code: 'MD01', label: 'MD01 – Kein gültiges Mandat' },
];

export const EBICS_STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  new: { label: 'Zugangsdaten erfasst', variant: 'outline' },
  keys_sent: { label: 'Warten auf Freischaltung', variant: 'warning' },
  bank_keys_pending: { label: 'Bankschlüssel prüfen', variant: 'info' },
  active: { label: 'Aktiv', variant: 'success' },
  suspended: { label: 'Pausiert', variant: 'secondary' },
};

export const BANK_STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  transmitted: { label: 'An Bank übertragen', variant: 'info' },
  processed: { label: 'Von Bank verarbeitet', variant: 'success' },
  rejected: { label: 'Von Bank abgelehnt', variant: 'destructive' },
};

export const MANDATE_STATUS: Record<MandateStatus, { label: string; variant: BadgeVariant }> = {
  active: { label: 'Aktiv', variant: 'success' },
  superseded: { label: 'Ersetzt', variant: 'secondary' },
  revoked: { label: 'Widerrufen', variant: 'destructive' },
  expired: { label: 'Verfallen', variant: 'secondary' },
};

export const MANDATE_CHANNEL_LABEL: Record<SepaMandate['channel'], string> = {
  website: 'Website-Anmeldung',
  portal: 'Kundenportal',
  office: 'Office (schriftlich)',
  contract_adjustment: 'Vertragsanpassung',
};

export const REFUND_STATUS: Record<RefundStatus, { label: string; variant: BadgeVariant }> = {
  open: { label: 'Offen', variant: 'outline' },
  in_run: { label: 'Im Überweisungslauf', variant: 'info' },
  paid: { label: 'Überwiesen', variant: 'success' },
  failed: { label: 'Zurückgekommen', variant: 'destructive' },
  cancelled: { label: 'Abgebrochen', variant: 'secondary' },
};

export const BANK_EVENT_STATUS: Record<BankEventStatus, { label: string; variant: BadgeVariant }> = {
  unmatched: { label: 'Nicht zugeordnet', variant: 'warning' },
  needs_review: { label: 'Prüfen', variant: 'warning' },
  matched: { label: 'Zugeordnet', variant: 'success' },
  ignored: { label: 'Ignoriert', variant: 'secondary' },
};

export const CHECKOUT_PROVIDER_LABEL: Record<CheckoutProvider, string> = {
  stripe: 'Stripe (Karte, SEPA über Stripe)',
  hausbank_sepa: 'SEPA-Lastschrift über die Hausbank',
};

export const SEPA_PROBLEM_LABEL: Record<string, string> = {
  creditor_id_missing: 'Gläubiger-ID fehlt',
  creditor_id_invalid: 'Gläubiger-ID ungültig',
  creditor_iban_missing: 'IBAN fehlt',
  creditor_name_missing: 'Gläubigername fehlt',
};

const IBAN_LENGTHS: Record<string, number> = {
  AD: 24, AT: 20, BE: 16, BG: 22, CH: 21, CY: 28, CZ: 24, DE: 22, DK: 18, EE: 20, ES: 24,
  FI: 18, FR: 27, GB: 22, GI: 23, GR: 27, HR: 21, HU: 28, IE: 22, IS: 26, IT: 27, LI: 21,
  LT: 20, LU: 20, LV: 21, MC: 27, MT: 31, NL: 18, NO: 15, PL: 28, PT: 25, RO: 24, SE: 24,
  SI: 19, SK: 24, SM: 27, VA: 22,
};

const compact = (value: string) => value.replace(/\s+/g, '').toUpperCase();

function mod97(text: string): number {
  let remainder = 0;
  for (const char of text) {
    const digits = /[A-Z]/.test(char) ? String(char.charCodeAt(0) - 55) : char;
    for (const digit of digits) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder;
}

/** Sofortprüfung im Formular; die verbindliche Prüfung macht das Backend. */
export function ibanProblem(value: string): string | null {
  const iban = compact(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) return 'Die IBAN hat kein gültiges Format.';
  const length = IBAN_LENGTHS[iban.slice(0, 2)];
  if (!length) return 'Die IBAN gehört nicht zum SEPA-Raum.';
  if (iban.length !== length) return `Eine IBAN aus ${iban.slice(0, 2)} hat ${length} Stellen.`;
  if (mod97(iban.slice(4) + iban.slice(0, 4)) !== 1) return 'Die Prüfziffer der IBAN stimmt nicht.';
  return null;
}

export function creditorIdProblem(value: string): string | null {
  const id = compact(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{3}[A-Z0-9]{1,28}$/.test(id)) return 'Format: z. B. DE98ZZZ09999999999';
  if (id.startsWith('DE') && id.length !== 18) return 'Eine deutsche Gläubiger-ID hat 18 Stellen.';
  if (mod97(id.slice(7) + id.slice(0, 4)) !== 1) return 'Die Prüfziffer der Gläubiger-ID stimmt nicht.';
  return null;
}

export function formatIban(value: string): string {
  return compact(value).replace(/(.{4})/g, '$1 ').trim();
}

export const MONTH_LABEL = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

export function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '–';
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

/** "1.234,50", "20", "20,5 €" → Cent; ungültige Eingaben ergeben null. */
export function parseEuroToCents(input: string): number | null {
  const compact = input.replace(/\s|€/g, '');
  if (compact === '') return null;
  const normalized = compact.includes(',')
    ? compact.replace(/\./g, '').replace(',', '.')
    : compact;
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

export function centsToEuroInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2).replace('.', ',');
}

export function tariffPriceLabel(
  tariff: Pick<Tariff, 'priceCents' | 'units' | 'contingentMode' | 'installmentCount'> &
    Partial<Pick<Tariff, 'model' | 'pricePeriodMonths'>>,
): string {
  if (tariff.model === 'periodic') {
    const period = PERIOD_LABEL[tariff.pricePeriodMonths ?? 1] ?? `${tariff.pricePeriodMonths} Monate`;
    return `${formatCents(tariff.priceCents)} je ${period}`;
  }
  if (tariff.contingentMode === 'usage_based') {
    const units = tariff.units && tariff.units > 1 ? tariff.units : 1;
    return `${formatCents(Math.round(tariff.priceCents / units))} je Einheit`;
  }
  const base = `${formatCents(tariff.priceCents)} für ${tariff.units ?? '?'} Einheiten`;
  if (tariff.contingentMode === 'installments' && tariff.installmentCount) {
    return `${base}, ${installmentLabel(tariff.priceCents, tariff.installmentCount)}`;
  }
  return base;
}

/** „3 × 33,33 €“ – bei ungeradem Betrag trägt die letzte Rate den Rest-Cent. */
export function installmentLabel(priceCents: number, count: number): string {
  if (count < 2) return formatCents(priceCents);
  const base = Math.floor(priceCents / count);
  const last = priceCents - base * (count - 1);
  return last === base
    ? `${count} × ${formatCents(base)}`
    : `${count - 1} × ${formatCents(base)} + ${formatCents(last)}`;
}

export const UNIT_ENTRY_KIND_LABEL: Record<UnitEntryKind, string> = {
  purchase: 'Paket',
  debit: 'Verbrauch',
  reversal: 'Rückbuchung',
  adjustment: 'Korrektur',
  forfeit: 'Verfall',
};

export const PACKAGE_SOURCE_LABEL: Record<string, string> = {
  enrollment: 'Anmeldung',
  followup: 'Folgepaket',
  office: 'Office',
  portal: 'Kundenportal',
};

/** Terminstatus, die eine Kontingent-Einheit verbrauchen (wie im Backend). */
export const CONSUMING_LESSON_STATUSES = ['durchgeführt', 'nicht_erschienen'] as const;

export const LESSON_STATUS_LABEL: Record<string, string> = {
  geplant: 'Geplant',
  durchgeführt: 'Durchgeführt',
  abgeschlossen: 'Durchgeführt',
  nicht_erschienen: 'Nicht erschienen',
  no_show: 'Nicht erschienen',
  abgesagt: 'Abgesagt',
  verschoben: 'Verschoben',
};

/** Heutiges Datum als JJJJ-MM-TT in lokaler Zeit (nicht UTC). */
export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function formatIsoDate(value: string | null | undefined): string {
  if (!value) return '–';
  const [year, month, day] = value.slice(0, 10).split('-');
  return year && month && day ? `${day}.${month}.${year}` : value;
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
